/**
 * Fixed-position style for a hover popover anchored to a badge in a table cell.
 *
 * Centred on the anchor and clamped 8px inside the viewport, so a badge in the last
 * column never pushes its popover off the right edge. Flips above the anchor when
 * there is less than `flipBelow` px below it.
 *
 * This is the placement the Areas and Residents device badges always used. The
 * settings tables had their own copy anchored to the badge's left edge with no
 * clamp, which is why theirs ran off-screen.
 *
 * The width is set explicitly because clamping needs to know it before the popover
 * renders. Pass the popover class's own CSS width: 300px sits inside
 * `.ap-device-popover`'s 14rem-22rem range; the count popovers are fixed at 16rem
 * (`ws-count-popover`) or 18rem (`up-users-`, `rp-group-residents-`, `ap-areas-`).
 *
 * `transform`, `margin` and `right` are reset because those count-popover classes
 * are written for absolute positioning — `left: 50%; transform: translateX(-50%)`
 * plus a `margin-top` — and left in place they would shift a fixed popover by half
 * its width.
 *
 * @param {Element|null} anchorEl
 * @param {{ width?: number, flipBelow?: number, gap?: number }} [opts]
 * @returns {object|null} style object, or null when there is no anchor
 */
export function anchoredPopoverStyle(anchorEl, { width = 300, flipBelow = 150, gap = 6 } = {}) {
    if (!anchorEl) return null;
    const rect = anchorEl.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const centerX = rect.left + rect.width / 2;
    const left = Math.max(8, Math.min(centerX - width / 2, window.innerWidth - width - 8));
    const above = spaceBelow < flipBelow;
    return {
        position: 'fixed',
        zIndex: 9999,
        left: `${left}px`,
        right: 'auto',
        width: `${width}px`,
        top: above ? 'auto' : `${rect.bottom + gap}px`,
        bottom: above ? `${window.innerHeight - rect.top + gap}px` : 'auto',
        margin: 0,
        transform: 'none',
    };
}
